import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/redux/Store';
import { FaSearch, FaCircle, FaStar } from 'react-icons/fa';
import { LoadingSpinner } from '@/components';
import { getMediaUrl } from '@/constants/media';
import { truncateName } from '@/utils/formatName';
import { formatRelativeTime } from '@/utils/formatTime';
import './ConversationsList.scss';

interface ConversationsListProps {
  selectedThreadId: string | null;
  onSelectThread: (threadId: string) => void;
}

const ConversationsList: React.FC<ConversationsListProps> = ({
  selectedThreadId,
  onSelectThread,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Get threads and current user from Redux store
  const threads = useAppSelector((state) => state.messages.threads);
  const isLoading = useAppSelector((state) => state.messages.loading.threads);
  const currentUser = useAppSelector((state) => state.auth?.user);

  // Filter threads based on search
  const filteredThreads = threads.filter(thread => {
    // Find the OTHER participant (not the current user)
    const otherParticipant = thread.participants.find(p => p.id !== currentUser?.id);
    const name = otherParticipant?.name?.toLowerCase() || '';
    const username = otherParticipant?.username?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return name.includes(query) || username.includes(query);
  });


  const truncateMessage = (message: string | undefined | null, maxLength: number = 50): string => {
    if (!message) return 'No messages yet';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <div className="conversations-list">
      {/* Header */}
      <div className="conversations-header">
        <h2>Messages</h2>
      </div>

      {/* Search */}
      <div className="conversations-search">
        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            id='messageSearch'
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Threads List */}
      <div className="conversations-threads">
        {isLoading ? (
          <LoadingSpinner size="medium" message="Loading conversations..." />
        ) : filteredThreads.length === 0 ? (
          <div className="conversations-empty">
            <p>
              {searchQuery
                ? 'No conversations found'
                : 'No messages yet. Start a conversation!'}
            </p>
          </div>
        ) : (
          filteredThreads.map((thread) => {
            const otherParticipant = thread.participants.find(p => p.id !== currentUser?.id);
            const isActive = thread.id === selectedThreadId;
            const hasUnread = thread.unreadCount > 0;
            const isSpotlighted = thread.lastMessage?.isSpotlighted && hasUnread;

            const handleAvatarClick = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (otherParticipant?.username) {
                navigate(`/profile/${otherParticipant.username}`);
              }
            };

            return (
              <button
                key={thread.id}
                className={`thread-item ${isActive ? 'active' : ''} ${hasUnread ? 'unread' : ''} ${isSpotlighted ? 'spotlighted' : ''}`}
                onClick={() => onSelectThread(thread.id)}
              >
                {/* Avatar */}
                <div
                  className="thread-avatar clickable"
                  onClick={handleAvatarClick}
                  title={`View ${otherParticipant?.name || 'user'}'s profile`}
                >
                  {otherParticipant?.avatar ? (
                    <img src={getMediaUrl(otherParticipant.avatar) || undefined} alt={otherParticipant.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {otherParticipant?.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  {hasUnread && <FaCircle className="online-indicator" />}
                  {isSpotlighted && <FaStar className="spotlight-indicator" />}
                </div>

                {/* Thread Info */}
                <div className="thread-info">
                  <div className="thread-header">
                    <span className="thread-name">
                      {isSpotlighted && <FaStar className="spotlight-name-icon" />}
                      {truncateName(otherParticipant?.name) || 'Unknown'}
                    </span>
                    <span className="thread-time">
                      {thread.lastMessage?.timestamp ? formatRelativeTime(thread.lastMessage.timestamp) : ''}
                    </span>
                  </div>
                  <div className="thread-preview">
                    <span className="preview-text">
                      {truncateMessage(thread.lastMessage?.content)}
                    </span>
                    {hasUnread && (
                      <span className={`unread-badge ${isSpotlighted ? 'spotlighted' : ''}`}>
                        {thread.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ConversationsList;
